import { useState } from "react"
import Button from "@/v2-backoffice-client/components/button"
import LayerOrganizeDialog from "@/v2-backoffice-client/layer/organize/LayerOrganizeDialog"
import TranslationPublishButton from "@/v2-backoffice-client/translation/publish/TranslationPublishButton"
import {
  LAYER_PAGE_TAB,
  type LayerPageTab,
} from "@/v2-backoffice-client/layer/layer.constants"
import { IS_CATALOG_AUTHORING_ENABLED } from "@/v2-backoffice-client/feature/feature.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { AppViewSheetKind } from "@/v2-backoffice-client/app/AppRouter"

export type LayerPageActionsProps = {
  tab: LayerPageTab
}

type CreateAction = {
  sheet: AppViewSheetKind
  label: string
  featureCode: string
}

const CREATE_LAYER: CreateAction = {
  sheet: "layerCreate",
  label: "New layer",
  featureCode: "UI_BackofficeLayers_Page_Create",
}

/**
 * What "new" means depends on which catalog is in view, so the create
 * button follows the tab instead of the page offering three of them at
 * once. The review states are views OF the map-layer catalog, so they keep
 * the map layer's; categories are seeded by the catalog and have no create
 * flow at all.
 */
const CREATE_ACTIONS: Record<LayerPageTab, CreateAction | null> = {
  [LAYER_PAGE_TAB.layers]: CREATE_LAYER,
  [LAYER_PAGE_TAB.review]: CREATE_LAYER,
  [LAYER_PAGE_TAB.rejected]: CREATE_LAYER,
  [LAYER_PAGE_TAB.basemaps]: {
    sheet: "basemapCreate",
    label: "New basemap",
    featureCode: "UI_BackofficeBasemaps_Page_Create",
  },
  [LAYER_PAGE_TAB.navLayers]: {
    sheet: "navLayerCreate",
    label: "New nav layer",
    featureCode: "UI_BackofficeNavLayers_Page_Create",
  },
  [LAYER_PAGE_TAB.categories]: null,
}

/** Ordering only means something where the map chooser reads it. */
const ORGANIZE_TABS = new Set<LayerPageTab>([
  LAYER_PAGE_TAB.layers,
  LAYER_PAGE_TAB.categories,
])

/**
 * The page's header actions. Organize (category + layer order) works
 * against the runtime on every deployment; creating catalog entries and
 * publishing the catalog need v2-features-db, so they are dev-only — the
 * same constraint as the Translation page.
 */
function LayerPageActions(props: LayerPageActionsProps) {
  const { tab } = props

  const opener = useAppViewSheetOpener()
  const [isOrganizeOpen, setIsOrganizeOpen] = useState(false)

  const create = CREATE_ACTIONS[tab]

  return (
    <>
      {ORGANIZE_TABS.has(tab) && (
        <Button
          size="sm"
          variant="secondary"
          iconSrc="bars-sort"
          data-feature-code="UI_BackofficeLayers_Page_Organize"
          onClick={() => setIsOrganizeOpen(true)}
        >
          Organize
        </Button>
      )}

      {IS_CATALOG_AUTHORING_ENABLED && (
        <>
          {create && (
            <Button
              size="sm"
              iconSrc="plus"
              data-feature-code={create.featureCode}
              // The guided create sheets ignore the id; the sheet system
              // needs SOME value to route.
              onClick={() => opener.open(create.sheet, "new")}
            >
              {create.label}
            </Button>
          )}
          <TranslationPublishButton />
        </>
      )}

      <LayerOrganizeDialog
        open={isOrganizeOpen}
        onOpenChange={setIsOrganizeOpen}
      />
    </>
  )
}

export default LayerPageActions
